import { computed, ref, watch } from 'vue'
import { useApolloClient } from '@vue/apollo-composable'
import {
  MY_USER_SETTINGS_QUERY,
  USER_SETTINGS_QUERY,
  SET_USER_SETTING_MUTATION,
  SET_USER_SETTING_FOR_MUTATION,
} from '~/graphql/user-settings'
import { useAuthStore } from '@/stores/authStore'

/**
 * Frontend mirror of the backend setting registry. Keys and defaults must stay
 * in sync with `utils/user-setting.constants.ts` in the backend.
 */
export const SETTING_KEYS = {
  GENERAL_SHOW_COMPANY_CASE_COUNTS: 'general.showCompanyCaseCounts',
  GENERAL_INCOMING_CALL_SOUND: 'general.incomingCallSound',

  STATS_HIDDEN_LEAD_AGENCY_IDS: 'stats.hiddenLeadAgencyIds',
  STATS_HIDDEN_COMPANY_IDS: 'stats.hiddenCompanyIds',
  STATS_DEFAULT_GROUP_BY: 'stats.defaultGroupBy',

  notificationsTracking: companyId => `notifications.tracking.${companyId}`,

  // Per-user display order, one key per entity type (e.g. order.Company).
  order: entityType => `order.${entityType}`,
}

export const SETTING_DEFAULTS = {
  [SETTING_KEYS.GENERAL_SHOW_COMPANY_CASE_COUNTS]: true,
  [SETTING_KEYS.GENERAL_INCOMING_CALL_SOUND]: true,
  [SETTING_KEYS.STATS_HIDDEN_LEAD_AGENCY_IDS]: [],
  [SETTING_KEYS.STATS_HIDDEN_COMPANY_IDS]: [],
  [SETTING_KEYS.STATS_DEFAULT_GROUP_BY]: 'day',
}

export const DEFAULT_NOTIFICATIONS_TRACKING = {
  trackingEnabled: true,
  webNotificationsEnabled: false,
  notificationEmail: '',
  notificationSmsNumber: '',
  notificationWhatsAppNumber: '',
}

const NOTIFICATIONS_TRACKING_PREFIX = 'notifications.tracking.'
const ORDER_PREFIX = 'order.'

export function getDefaultFor(key) {
  if (key.startsWith(NOTIFICATIONS_TRACKING_PREFIX))
    return DEFAULT_NOTIFICATIONS_TRACKING

  if (key.startsWith(ORDER_PREFIX)) return []

  return SETTING_DEFAULTS[key]
}

/**
 * Builds the common settings API given a load/save pair. Both
 * `useUserPreferences` (current user, singleton) and `useUserSettings`
 * (admin viewing another user, per-instance) share this core. Exported so the
 * get/set semantics (defaults, optimistic write + rollback, typed accessors)
 * are unit-tested with a fake state/ops pair, no Apollo needed.
 */
export function createSettingsAPI(state, ops) {
  const { settingsMap, loaded, loading } = state
  const { load, save } = ops

  /** Reads a setting; returns the user's value or the registered default. */
  function get(key, explicitDefault) {
    if (key in settingsMap.value) return settingsMap.value[key]
    if (explicitDefault !== undefined) return explicitDefault
    
    return getDefaultFor(key)
  }

  /** Persists a setting and updates the local cache optimistically. */
  async function set(key, value) {
    const previous = settingsMap.value[key]

    settingsMap.value = { ...settingsMap.value, [key]: value }

    try {
      await save(key, value)
    }
    catch (err) {
      if (previous === undefined) {
        const next = { ...settingsMap.value }

        delete next[key]
        settingsMap.value = next
      }
      else {
        settingsMap.value = { ...settingsMap.value, [key]: previous }
      }
      console.error('[settings] Failed to save', key, err)
      throw err
    }
  }

  const showCompanyCaseCounts = computed({
    get: () => get(SETTING_KEYS.GENERAL_SHOW_COMPANY_CASE_COUNTS) !== false,
    set: v => set(SETTING_KEYS.GENERAL_SHOW_COMPANY_CASE_COUNTS, !!v),
  })

  // Ring the standard sound when the incoming-call banner lands. Mutable here.
  const incomingCallSound = computed({
    get: () => get(SETTING_KEYS.GENERAL_INCOMING_CALL_SOUND) !== false,
    set: v => set(SETTING_KEYS.GENERAL_INCOMING_CALL_SOUND, !!v),
  })

  const statsHiddenLeadAgencyIds = computed({
    get: () => get(SETTING_KEYS.STATS_HIDDEN_LEAD_AGENCY_IDS) ?? [],
    set: v => set(SETTING_KEYS.STATS_HIDDEN_LEAD_AGENCY_IDS, Array.isArray(v) ? v.map(String) : []),
  })

  const statsHiddenCompanyIds = computed({
    get: () => get(SETTING_KEYS.STATS_HIDDEN_COMPANY_IDS) ?? [],
    set: v => set(SETTING_KEYS.STATS_HIDDEN_COMPANY_IDS, Array.isArray(v) ? v.map(String) : []),
  })

  const statsDefaultGroupBy = computed({
    get: () => get(SETTING_KEYS.STATS_DEFAULT_GROUP_BY) ?? 'day',
    set: v => set(SETTING_KEYS.STATS_DEFAULT_GROUP_BY, v),
  })

  function notificationsTrackingFor(companyId) {
    const key = SETTING_KEYS.notificationsTracking(companyId)
    
    return computed({
      get: () => ({
        ...DEFAULT_NOTIFICATIONS_TRACKING,
        ...(get(key) ?? {}),
      }),
      set: v => set(key, { ...DEFAULT_NOTIFICATIONS_TRACKING, ...(v ?? {}) }),
    })
  }

  /**
   * The user's display order for an entity type (e.g. 'Company') as an array of
   * string ids, get/set. Empty when the user hasn't ordered it — callers fall
   * back to the entity's global `displayOrder`.
   */
  function orderFor(entityType) {
    const key = SETTING_KEYS.order(entityType)
    
    return computed({
      get: () => {
        const v = get(key)
        
        return Array.isArray(v) ? v.map(String) : []
      },
      set: v => set(key, Array.isArray(v) ? v.map(String) : []),
    })
  }

  return {
    loading,
    loaded,
    loadAll: load,
    get,
    set,
    showCompanyCaseCounts,
    incomingCallSound,
    statsHiddenLeadAgencyIds,
    statsHiddenCompanyIds,
    statsDefaultGroupBy,
    notificationsTrackingFor,
    orderFor,
  }
}

// ── Current user (singleton) ─────────────────────────────────────────────────

const _settingsMap = ref({})
const _loaded = ref(false)
const _loading = ref(false)

export function useUserPreferences() {
  const { resolveClient } = useApolloClient()
  const authStore = useAuthStore()

  async function load() {
    if (!authStore.isAuthenticated) {
      _settingsMap.value = {}
      _loaded.value = false
      
      return
    }

    _loading.value = true
    try {
      const client = resolveClient()

      const { data } = await client.query({
        query: MY_USER_SETTINGS_QUERY,
        fetchPolicy: 'network-only',
      })

      _settingsMap.value = data?.myUserSettings ?? {}
      _loaded.value = true
    }
    catch (err) {
      console.error('[useUserPreferences] Failed to load settings', err)
      _settingsMap.value = {}
    }
    finally {
      _loading.value = false
    }
  }

  async function save(key, value) {
    const client = resolveClient()

    await client.mutate({
      mutation: SET_USER_SETTING_MUTATION,
      variables: { key, value },
    })
  }

  watch(
    () => authStore.isAuthenticated,
    isAuth => {
      if (isAuth && !_loaded.value) load()
      else if (!isAuth) {
        _settingsMap.value = {}
        _loaded.value = false
      }
    },
    { immediate: true },
  )

  return createSettingsAPI(
    { settingsMap: _settingsMap, loaded: _loaded, loading: _loading },
    { load, save },
  )
}

// ── Arbitrary user (admin-only, per-instance) ───────────────────────────────

/**
 * SysAdmin-only view of another user's settings. Per-instance state — each
 * call returns its own cache and reload pair. Auto-loads on mount; reloads
 * when `userId` changes.
 */
export function useUserSettings(userId) {
  const { resolveClient } = useApolloClient()

  const settingsMap = ref({})
  const loaded = ref(false)
  const loading = ref(false)

  function currentUserId() {
    const raw = typeof userId === 'function'
      ? userId()
      : (userId?.value !== undefined ? userId.value : userId)

    
    return raw == null ? null : String(raw)
  }

  async function load() {
    const uid = currentUserId()
    if (!uid) {
      settingsMap.value = {}
      loaded.value = false
      
      return
    }

    loading.value = true
    try {
      const client = resolveClient()

      const { data } = await client.query({
        query: USER_SETTINGS_QUERY,
        variables: { userId: uid },
        fetchPolicy: 'network-only',
      })

      settingsMap.value = data?.userSettingsFor ?? {}
      loaded.value = true
    }
    catch (err) {
      console.error('[useUserSettings] Failed to load settings for', uid, err)
      settingsMap.value = {}
    }
    finally {
      loading.value = false
    }
  }

  async function save(key, value) {
    const uid = currentUserId()
    if (!uid) throw new Error('useUserSettings: no userId')
    const client = resolveClient()

    await client.mutate({
      mutation: SET_USER_SETTING_FOR_MUTATION,
      variables: { userId: uid, key, value },
    })
  }

  // Auto-reload on userId change (handles refs / getters).
  watch(
    () => currentUserId(),
    uid => {
      if (uid) load()
      else {
        settingsMap.value = {}
        loaded.value = false
      }
    },
    { immediate: true },
  )

  return createSettingsAPI(
    { settingsMap, loaded, loading },
    { load, save },
  )
}
